import React from "react";
import { ITabProps, Tabs } from "component-library";

import { sortArray } from "shared/utils/array";

import { Credit, CreditsList } from "models";

import { Action } from "components/common/Action/Action";
import { EmptyData } from "components/common/EmptyData";

import { CreditsGrid } from "../CreditsGrid";
import { EditPanel } from "../EditPanel";

import { ICreditsSectionContent } from "./CreditsSectionContent";

import "./CreditsSection.scss";

enum ViewMode {
  tabs,
  fullList,
}

enum SortingMode {
  date,
  position,
}

const FURTHER_ID = -1;
const INCREMENT = 10;
const YEAR_1900 = -2208988800;

export interface ICreditsSectionProps {
  editCredits: () => void;
  content: ICreditsSectionContent;
  creditsList?: CreditsList;
  isReadonly: boolean;
}

interface ICreditsSectionState {
  selectedTypeId?: number;
  currentRowsNumber: number;
  viewMode: ViewMode;
  sortingMode: SortingMode;
}

export class CreditsSection extends React.Component<
  ICreditsSectionProps,
  ICreditsSectionState
> {
  public readonly state: Readonly<ICreditsSectionState> = {
    currentRowsNumber: INCREMENT,
    viewMode: ViewMode.tabs,
    sortingMode: SortingMode.position,
  };

  public render() {
    const { editCredits, content, isReadonly } = this.props;

    return (
      <div className="c-credits-section">
        <EditPanel
          header={content.header}
          editFn={editCredits}
          hideEdit={isReadonly}
          editLinkAltText={content.editLinkAltText}
        />
        {this.renderContent()}
      </div>
    );
  }

  public static getDerivedStateFromProps(
    { creditsList }: ICreditsSectionProps,
    { selectedTypeId }: ICreditsSectionState
  ) {
    const state: Partial<ICreditsSectionState> = {};

    if (selectedTypeId === undefined && creditsList) {
      const firstHeading = creditsList.creditHeadings
        .sort((left, right) => (left.position > right.position ? 1 : -1))
        .find((heading) => !heading.isHidden);

      state.selectedTypeId = firstHeading
        ? firstHeading.creditType.id
        : FURTHER_ID;
    }

    return state;
  }

  private renderContent = () => {
    let contentNode: React.ReactNode = null;
    const { creditsList, content } = this.props;

    if (creditsList) {
      if (creditsList.credits.length > 0) {
        const { currentRowsNumber, viewMode } = this.state;

        const credits = this.getCredits();

        const headings = creditsList.creditHeadings || [];

        const hasAnyVisibleHeading = headings.some(
          (heading) => !heading.isHidden
        );

        const isTabsVisible =
          viewMode !== ViewMode.fullList && hasAnyVisibleHeading;

        contentNode = (
          <React.Fragment>
            {isTabsVisible && this.renderTabs()}
            {this.renderAdditionalControls()}
            <CreditsGrid
              content={content}
              credits={credits}
              rowsAmount={currentRowsNumber}
              headings={headings}
            />
            {this.renderNavigation(credits)}
          </React.Fragment>
        );
      } else {
        contentNode = (
          <EmptyData theme="light" text={content.noCreditsPlaceholder} />
        );
      }
    }

    return contentNode;
  };

  private renderAdditionalControls = () => {
    const {
      isReadonly,
      content: {
        viewWithoutTabs,
        viewWithTabs,
        viewInDateOrder,
        viewUserOrder,
      },
      creditsList,
    } = this.props;
    const { sortingMode, viewMode } = this.state;

    let component: React.ReactNode = null;

    if (isReadonly && creditsList) {
      const isViewModeAvailable = this.getVisibleTabCounts(creditsList) > 1;

      const nextSortingMode =
        sortingMode === SortingMode.date
          ? SortingMode.position
          : SortingMode.date;
      const nextViewMode =
        viewMode === ViewMode.tabs ? ViewMode.fullList : ViewMode.tabs;

      component = (
        <div className="c-credits-section__additional-panel">
          {isViewModeAvailable && (
            <React.Fragment>
              <Action
                className="g-alternative-link g-alternative-link--md"
                onClick={this.setViewMode(nextViewMode)}
              >
                {nextViewMode === ViewMode.fullList
                  ? viewWithoutTabs
                  : viewWithTabs}
              </Action>
              |
            </React.Fragment>
          )}
          <a
            className="g-alternative-link g-alternative-link--md"
            onClick={this.setSortingMode(nextSortingMode)}
            tabIndex={0}
          >
            {nextSortingMode === SortingMode.date
              ? viewInDateOrder
              : viewUserOrder}
          </a>
        </div>
      );
    }

    return component;
  };

  private renderNavigation = (credits: Credit[]) => {
    const isNavVisible = credits.length > INCREMENT;
    const { currentRowsNumber } = this.state;
    const { content } = this.props;

    const buttons: React.ReactNode[] = [];

    if (isNavVisible) {
      if (currentRowsNumber < credits.length) {
        buttons.push(
          this.renderNavButton(content.showMore, () =>
            this.setRowsNumber(INCREMENT + currentRowsNumber)
          )
        );
      }

      if (currentRowsNumber > INCREMENT) {
        if (buttons.length > 0) {
          buttons.push("|");
        }

        buttons.push(
          this.renderNavButton(content.showFewer, () =>
            this.setRowsNumber(INCREMENT)
          )
        );
      }

      if (currentRowsNumber < credits.length) {
        if (buttons.length > 0) {
          buttons.push("|");
        }

        buttons.push(
          this.renderNavButton(content.showAll, () =>
            this.setRowsNumber(credits.length)
          )
        );
      }
    }

    return <div className="c-credits-section__nav">{buttons}</div>;
  };

  private renderNavButton = (text: string, clickHandler: () => void) => (
    <Action
      className="g-alternative-link g-alternative-link--md"
      onClick={clickHandler}
      key={text}
    >
      {text}
    </Action>
  );

  private setViewMode = (viewMode: ViewMode) => () =>
    this.setState({ viewMode });

  private setSortingMode = (sortingMode: SortingMode) => () =>
    this.setState({ sortingMode });

  private setRowsNumber = (rowsNumber: number) =>
    this.setState({ currentRowsNumber: rowsNumber });

  private renderTabs = () => {
    const tabs = this.getTabs();

    const isVisible = tabs.length > 0;

    return (
      <div className="c-credits-section__tabs">
        {isVisible && <Tabs tabs={tabs} />}
      </div>
    );
  };

  private getTabs = () => {
    const { creditsList, content } = this.props;
    const { selectedTypeId } = this.state;

    let tabs: ITabProps[] = [];

    if (creditsList) {
      tabs = creditsList.creditHeadings
        .sort((left, right) => (left.position > right.position ? 1 : -1))
        .filter((creditHeading) => !creditHeading.isHidden)
        .map(({ creditType }) => ({
          isActive: selectedTypeId === creditType.id,
          text: creditType.name,
          click: () => this.selectTab(creditType.id),
        }));

      if (creditsList.creditHeadings.some((heading) => heading.isHidden)) {
        tabs.push({
          isActive: selectedTypeId === FURTHER_ID,
          text: content.furtherTab,
          click: () => this.selectTab(FURTHER_ID),
        });
      }
    }

    return tabs;
  };

  private getCredits = () => {
    const { creditsList } = this.props;

    let credits: Credit[] = [];

    if (creditsList) {
      const { selectedTypeId, viewMode, sortingMode } = this.state;

      switch (viewMode) {
        case ViewMode.tabs:
          credits = this.getListByTypeId(creditsList, selectedTypeId);
          break;
        case ViewMode.fullList:
          credits = this.sortFullList(creditsList);
          break;
      }

      switch (sortingMode) {
        case SortingMode.position:
          if (ViewMode.fullList !== viewMode) {
            credits = credits.sort(
              (left, right) => left.position - right.position
            );
          }
          break;
        case SortingMode.date:
          credits = this.sortCreditsByDate(credits);
          break;
      }
    }

    return credits;
  };

  private getListByTypeId = (
    creditsList: CreditsList,
    selectedTypeId?: number
  ) => {
    let credits: Credit[] = [];

    if (selectedTypeId !== FURTHER_ID) {
      credits = creditsList.credits.filter(
        (creditHeading) => creditHeading.typeId === selectedTypeId
      );
    } else {
      credits = this.getFurtherCredits(creditsList);
    }

    return credits;
  };

  private sortCreditsByDate = (credits: Credit[]) => {
    return sortArray(credits, [
      {
        expression: (credit) => new Date(credit.dateFrom || YEAR_1900),
        order: "desc",
      },
      {
        expression: (credit) =>
          credit.dateFrom ? 0 : new Date(credit.dateTo || YEAR_1900),
        order: "desc",
      },
      { expression: (credit) => credit.name.toLowerCase(), order: "asc" },
    ]);
  };

  private sortFullList = (creditsList: CreditsList) => {
    const furtherCredits = this.getFurtherCredits(creditsList).sort(
      (left, right) => left.position - right.position
    );

    const headingSequence = creditsList.creditHeadings
      .sort((left, right) => (left.position > right.position ? 1 : -1))
      .filter((creditHeading) => !creditHeading.isHidden);

    const fullCreditsList: Credit[] = [];

    headingSequence.forEach((heading) => {
      const credits = creditsList.credits
        .filter(
          (creditHeading) => creditHeading.typeId === heading.creditType.id
        )
        .sort((left, right) => (left.position > right.position ? 1 : -1));

      fullCreditsList.push(...credits);
    });

    fullCreditsList.push(...furtherCredits);

    return fullCreditsList;
  };

  private getFurtherCredits = (creditsList: CreditsList) => {
    const furtherCategories = creditsList.creditHeadings
      .filter((creditHeading) => creditHeading.isHidden)
      .map(({ creditType: { id } }) => id);

    return creditsList.credits.filter((creditHeading) =>
      furtherCategories.some((id) => creditHeading.typeId === id)
    );
  };

  private selectTab = (tabId: number) =>
    this.setState({ selectedTypeId: tabId, currentRowsNumber: INCREMENT });

  private getVisibleTabCounts = (creditsList: CreditsList) => {
    const hasFurtherTab = creditsList.creditHeadings.some(
      (heading) => heading.isHidden
    );
    const visibleHeadingsCount = creditsList.creditHeadings.filter(
      (heading) => !heading.isHidden
    ).length;

    return visibleHeadingsCount + Number(hasFurtherTab);
  };
}
